type CountryCode = {name: string; dial_code: string; code?: string};

/**
 * Build picker items that lead with the dial code, then the country name
 * ("+91 · India"). Dial-code-first keeps the compact selected chip readable
 * (it always shows the dial code even when the name is clipped) while the
 * full label stays searchable by country name and disambiguates shared codes
 * (e.g. "+1" US/Canada vs "+1684").
 */
export const toCountryPickerItems = (
  countryCodes: CountryCode[],
): {label: string; value: string}[] =>
  countryCodes.map(c => ({
    label: `${c.dial_code} · ${c.name}`,
    value: c.dial_code,
  }));

/** Restore the last-used dial code so returning intl users don't re-select it. */
export const resolveInitialCountryCode = (
  stored: string | null | undefined,
): string => (stored && /^\+\d+$/.test(stored) ? stored : '+91');

type SendOtpResponse =
  | {success?: boolean; error?: string; tnc?: number}
  | undefined
  | null;

/**
 * react-query fires a mutation's onSuccess whenever the request resolves, even
 * for an app-level failure like "Onboarding halted". Callers must not treat
 * such a response as a sent OTP (which would start the resend timer).
 */
export const interpretSendOtpResponse = (
  response: SendOtpResponse,
): {onboardingHalted: boolean; errorMessage: string} => {
  const failed = response?.success === false;
  const halted = failed && response?.error === 'Onboarding halted';
  return {
    onboardingHalted: halted,
    // Every OTHER app-level failure (HTTP 200 carrying success:false, e.g. a
    // wait-before-retrying message) used to fall through as a sent OTP. Relay
    // the server's own wording so the user knows what to do about it.
    errorMessage:
      failed && !halted
        ? extractApiErrorMessage(response, GENERIC_SEND_OTP_ERROR)
        : '',
  };
};

/**
 * AWS WAF rate-limits the OTP endpoints per source IP (see infra-pulumi/waf)
 * and answers a blocked request with its own HTML page, so there is no server
 * message to relay — say what actually happened instead of a generic failure.
 */
export const OTP_RATE_LIMIT_MESSAGE =
  'Too many attempts. Please wait a minute before requesting another OTP.';

const GENERIC_SEND_OTP_ERROR = 'Could not send the OTP. Please try again.';

/**
 * A message is only worth showing if it came from our API. Rejects the WAF's
 * HTML body, axios' own "Request failed with status code 500" boilerplate, and
 * anything too long to sit on one line of the login form.
 */
const isUsableMessage = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= 200 &&
    !trimmed.startsWith('<') &&
    !/^Request failed with status code/i.test(trimmed)
  );
};

/**
 * Pull the server's own explanation out of a failed OTP call so the user sees
 * *why* it failed (wrong password, wait N seconds, ...) instead of a blanket
 * "an error occurred". ChatRestClient's response interceptor rejects with
 * `error.response.data`, so an API failure arrives here as the bare
 * `{success: false, error: '...'}` body; a raw axios error (network blip, or a
 * client without that interceptor) still nests it under `response.data`.
 */
export const extractApiErrorMessage = (
  error: any,
  fallback: string = GENERIC_SEND_OTP_ERROR,
): string => {
  const body = error?.response?.data ?? error;
  // The API's own wording wins even on a 429: otp_guard answers a cooldown
  // with the exact wait ("Please wait 45 seconds before requesting another
  // OTP."), which is the whole point of showing the server's message.
  const candidate = [
    body?.error,
    body?.message,
    body?.detail,
    error?.message,
  ].find(isUsableMessage);
  if (candidate) {
    return candidate.trim();
  }
  // No usable body: 429 carries `retry_after` seconds alongside it, and 403 is
  // the WAF block action on these endpoints (an HTML page, no message at all).
  const status = error?.status ?? error?.response?.status;
  const retryAfter = Number(body?.retry_after);
  if (status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
    return `Please wait ${Math.ceil(
      retryAfter,
    )} seconds before requesting another OTP.`;
  }
  if (status === 429 || status === 403) {
    return OTP_RATE_LIMIT_MESSAGE;
  }
  return fallback;
};
